import { action, makeObservable, observable } from 'mobx';

import { ENDPOINTS } from '@/configs/endpoints';
import { TREE_ROOT_NODE_ID, TreeNode, TreeNodeApi, treeNodeSchema } from '@/entities/Tree';
import { ILocalStore } from '@/stores/interfaces/ILocalStore';
import { PdfNotesApi } from '@/stores/local/shared/PdfNotesStore';
import { TreeStore } from '@/stores/local/shared/TreeStore';

import { DocumentsTreeSelectionStore } from '../../shared/DocumentsTreeSelectionStore';
import { SearchDocumentStore } from '../SearchDocumentStore';

import { PdfNotesIndexStore } from './PdfNotesIndexStore';
import { DocumentsPermissionsStore, type PermissionsRequestBody } from './PermissionsStore';
import { PopupsStore } from './PopupsStore/PopupsStore';

type SidebarDocumentsStoreParams = {
  organizationId: number;
  getOrganizationName?: () => string | null;
};

export class SidebarDocumentsStore implements ILocalStore {
  readonly treeStore: TreeStore;
  readonly searchDocumentStore: SearchDocumentStore;
  readonly selectionStore: DocumentsTreeSelectionStore;
  readonly popups: PopupsStore;
  readonly permissionsStore: DocumentsPermissionsStore;
  readonly pdfNotesIndex: PdfNotesIndexStore;
  readonly pdfNotesApi: PdfNotesApi;

  readonly organizationId: number;
  private readonly _getOrganizationId: () => number;
  private readonly _getOrganizationName: () => string | null;

  isContextSelectionEnabled: boolean = false;

  constructor({ organizationId, getOrganizationName }: SidebarDocumentsStoreParams) {
    this.organizationId = SidebarDocumentsStore._normalizeOrganizationId(organizationId);
    this._getOrganizationId = () => this.organizationId;
    this._getOrganizationName = getOrganizationName ?? (() => null);

    makeObservable(this, {
      isContextSelectionEnabled: observable,
      toggleContextSelection: action.bound,
    });

    this.permissionsStore = new DocumentsPermissionsStore({
      getOrganizationId: this._getOrganizationId,
    });
    const documentsEndpoint = ENDPOINTS.documents.tree(String(this.organizationId));

    this.treeStore = new TreeStore({
      level: 0,
      data: this._createRootNode(),
      normalizeData: (node: TreeNodeApi) => {
        const result = treeNodeSchema.safeParse(node);

        if (result.success) {
          return result.data;
        }

        return null;
      },
      endpoint: documentsEndpoint,
      getOrganizationId: this._getOrganizationId,
      onAfterFetch: this._permissionsAfterTreeFetch,
    });
    this.searchDocumentStore = new SearchDocumentStore({
      getOrganizationId: this._getOrganizationId,
    });
    this.pdfNotesApi = new PdfNotesApi();
    this.pdfNotesIndex = new PdfNotesIndexStore({
      organizationId: String(this.organizationId),
      api: this.pdfNotesApi,
    });
    this.selectionStore = new DocumentsTreeSelectionStore({ treeStore: this.treeStore, isSelectAll: true });
    this.popups = new PopupsStore({
      getOrganizationId: this._getOrganizationId,
      getOrganizationName: this._getOrganizationName,
      treeStore: this.treeStore,
      selectionStore: this.selectionStore,
    });
  }

  init = async (): Promise<void> => {
    if (!this.organizationId) {
      return;
    }

    if (!this.pdfNotesIndex.meta.isLoading && !this.pdfNotesIndex.meta.isLoaded) {
      this.pdfNotesIndex.load();
    }

    if (this.treeStore.meta.isLoading || this.treeStore.meta.isLoaded) {
      return;
    }

    await this.treeStore.fetchTree();
  };

  private static _normalizeOrganizationId(id: number | null | undefined): number {
    return typeof id === 'number' && Number.isFinite(id) ? Number(id) : 0;
  }

  private _createRootNode = (): TreeNode => ({
    id: TREE_ROOT_NODE_ID,
    name: 'Документы',
    type: 'folder',
    hasChildren: true,
    isPersonal: false,
    contentAvailable: true,
    availabilityBlockReason: null,
  });

  async openFolderPath(folderPath: Array<string | number>): Promise<void> {
    const normalizedPath = folderPath.map((id) => String(id)).filter((id) => id && id !== TREE_ROOT_NODE_ID);

    if (normalizedPath.length === 0) {
      return;
    }

    let currentStore: TreeStore | null = this.treeStore;

    for (const folderId of normalizedPath) {
      if (!currentStore) {
        return;
      }

      await currentStore.open();

      currentStore =
        currentStore.children.find((child) => child.data.type === 'folder' && String(child.data.id) === folderId) ??
        null;

      if (!currentStore) {
        return;
      }
    }

    if (currentStore) {
      await currentStore.open();
    }
  }

  private _permissionsAfterTreeFetch = (nodes: TreeNode[]): void => {
    if (!this.organizationId || nodes.length === 0) {
      return;
    }

    const requestItems: PermissionsRequestBody[] = nodes.map((node) => ({
      id: node.id,
      type: node.type === 'file' ? 'document' : 'folder',
    }));

    if (requestItems.length === 0) {
      return;
    }

    this.permissionsStore.fetchPermissions(requestItems);
  };

  toggleContextSelection(): void {
    this.isContextSelectionEnabled = !this.isContextSelectionEnabled;
  }

  destroy(): void {
    this.popups.destroy();
    this.selectionStore.destroy();
    this.searchDocumentStore.destroy();
    this.pdfNotesIndex.destroy();
    this.treeStore.destroy();
    this.permissionsStore.destroy();
  }
}
